import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "@/lib/AuthContext";
import { IssueRow } from "./IssueDialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import CardReadiness from "./CardReadiness";
import {
  MapPin, User, Phone, Mail, ChevronDown, ChevronRight, X,
  AlertTriangle, Wrench, Building2, Send, Trash2, Pencil, Save, Plus, HardHat,
} from "lucide-react";
import { toast } from "sonner";

// Lineout fields surfaced inside the Mob Lineout section.
// `level: "project"` → notes saved with scope_type="" (shared across every mob
//   in this project regardless of scope).
// `level: "scope"`   → notes saved with scope_type=<mob.scope_type> (shared
//   across mobs of the same scope only).
// `auto: "daily_target"` → auto-fill is computed client-side from
//   mob.total_quantity and total scheduled hours.
// `auto: "last_area"`    → auto-fill comes from /projects/{id}/last-dispatched.
export function workdaysBetween(startIso, endIso) {
  if (!startIso || !endIso) return 0;
  const s = new Date(startIso + "T00:00:00");
  const e = new Date(endIso + "T00:00:00");
  if (e < s) return 0;
  let n = 0;
  for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
    const day = d.getDay();
    if (day !== 0 && day !== 6) n += 1;
  }
  return n;
}

export const LINEOUT_FIELDS = [
  { key: "parking_logistics",     label: "Parking and Site Logistics",  level: "project" },
  { key: "current_install_plan",  label: "Special Instructions",        level: "project" },
  { key: "daily_target",          label: "Daily Target",                 level: "scope",   auto: "daily_target", placement: "after_materials" },
  { key: "installer_notes",       label: "Important Installer Notes",   level: "project" },
  { key: "last_area_worked_in",   label: "Last Area Worked In",          level: "project", auto: "last_area" },
];

function fmtTimestamp(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
}

function Section({ label, count, headerExtra, children, defaultOpen = false, openOverride, testid }) {
  const [open, setOpen] = useState(defaultOpen);
  useEffect(() => {
    if (openOverride !== undefined) setOpen(!!openOverride);
  }, [openOverride]);
  return (
    <div className="border-t border-gray-100">
      <div className="w-full flex items-center justify-between px-3 py-2 hover:bg-gray-50">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-2 text-sm font-semibold text-foreground/90"
          data-testid={testid}
        >
          {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          {label}{typeof count === "number" ? <span className="text-gray-500 font-normal">({count})</span> : null}
        </button>
        {headerExtra}
      </div>
      {open && <div className="px-3 pb-3">{children}</div>}
    </div>
  );
}

// Renders FRS items grouped by room, each room collapsible.
function MaterialsBreakdown({ frsItems }) {
  const groups = useMemo(() => {
    const m = new Map();
    for (const it of frsItems || []) {
      const key = `${it.building || ""}||F${it.floor || ""}||${it.room_number || ""}`;
      if (!m.has(key)) {
        m.set(key, {
          key, building: it.building || "", floor: it.floor || "",
          room: it.room_number || "", items: [], totalQty: 0,
        });
      }
      const g = m.get(key);
      g.items.push(it);
      g.totalQty += Number(it.quantity || 0);
    }
    return [...m.values()].sort((a, b) =>
      (a.building || "").localeCompare(b.building || "") ||
      String(a.floor).localeCompare(String(b.floor)) ||
      String(a.room).localeCompare(String(b.room))
    );
  }, [frsItems]);
  if (!groups.length) return <div className="text-xs text-gray-400">No FRS items linked.</div>;
  return (
    <div className="space-y-1">
      {groups.map((g) => <RoomRow key={g.key} g={g} />)}
    </div>
  );
}

function RoomRow({ g }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-100 rounded">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-2 py-1 text-[12px] hover:bg-gray-50"
        data-testid={`mob-room-${g.key}`}
      >
        <span className="flex items-center gap-1.5">
          {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          <Building2 className="w-3 h-3 text-gray-500" />
          <span className="font-medium text-foreground/90">
            {g.building}{g.floor ? ` · F${g.floor}` : ""}{g.room ? ` · Rm ${g.room}` : ""}
          </span>
        </span>
        <span className="text-[11px] text-gray-500">{g.items.length} line{g.items.length === 1 ? "" : "s"} · {g.totalQty}u</span>
      </button>
      {open && (
        <table className="w-full text-[11px]">
          <thead className="bg-gray-50 text-gray-500">
            <tr><th className="text-left px-2 py-1 font-medium">Description</th><th className="text-left px-2 py-1 font-medium w-20">Part</th><th className="text-right px-2 py-1 font-medium w-12">Qty</th></tr>
          </thead>
          <tbody>
            {g.items.map((i) => (
              <tr key={i.id} className="border-t border-gray-100">
                <td className="px-2 py-1 text-foreground/80">{i.product_description}</td>
                <td className="px-2 py-1 text-gray-500 font-mono">{i.product_code || "—"}</td>
                <td className="px-2 py-1 text-right font-medium">{i.quantity}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// One note field with timestamped append-only history. The header shows a
// small "+" button that reveals the new-note textarea (hidden by default to
// save vertical space). Old entries are read-only with an Edit button.
// Newest entry is shown on TOP. When `autoFill` is provided AND no entries
// exist yet, the textarea pre-fills with it on first open.
//
// Special-case: when `compactSummary` is provided (e.g. for Daily Target), the
// section header renders a compact summary value next to the label that
// becomes click-to-edit instead of using the full add-note flow.
export function NoteHistoryField({
  projectId, mobScopeType, fieldKey, label, level, autoFill, compactSummary,
  notes, currentUser, onAdded, onEdited,
  openOverride, onDraftChange,
}) {
  const [open, setOpen] = useState(false);
  // Sync from the master Expand/Collapse-All control. Once mirrored locally
  // each section is fully click-toggleable independently of the master.
  useEffect(() => {
    if (openOverride !== undefined) setOpen(!!openOverride);
  }, [openOverride]);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState("");
  const scopeForSave = level === "project" ? "" : (mobScopeType || "");
  // Newest on top
  const visible = useMemo(() => {
    return (notes || [])
      .filter((n) => n.key === fieldKey)
      .filter((n) => level === "project" ? n.scope_type === "" : n.scope_type === (mobScopeType || ""))
      .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  }, [notes, fieldKey, level, mobScopeType]);
  // First time the user opens "add" with no prior entries, pre-fill the
  // suggestion (foreman edits before saving).
  useEffect(() => {
    if (adding && autoFill && !draft && visible.length === 0) {
      setDraft(autoFill);
    }
    // eslint-disable-next-line
  }, [adding, autoFill]);
  const submit = async () => {
    if (!draft.trim()) return;
    setSaving(true);
    try {
      const r = await api.post("/notes", {
        project_id: projectId,
        scope_type: scopeForSave,
        key: fieldKey,
        text: draft.trim(),
        author: currentUser || "",
      });
      onAdded && onAdded(r.data);
      setDraft("");
      onDraftChange && onDraftChange(fieldKey, "");
      setAdding(false);
      toast.success(`${label} note added`);
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };
  const saveEdit = async (noteId) => {
    if (!editDraft.trim()) return;
    try {
      const r = await api.patch(`/notes/${noteId}`, { text: editDraft.trim() });
      onEdited && onEdited(r.data);
      setEditingId(null);
      setEditDraft("");
      toast.success("Note updated");
    } catch { toast.error("Update failed"); }
  };
  const levelBadge = level === "scope"
    ? <span className="text-[9px] uppercase font-semibold text-purple-700 bg-purple-50 border border-purple-200 rounded px-1.5">Scope</span>
    : null;
  return (
    <div className="border border-gray-100 rounded">
      <div className="w-full flex items-center justify-between px-2 py-1.5 hover:bg-gray-50">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex-1 flex items-center gap-1.5 text-[12px] text-left"
          data-testid={`lineout-toggle-${fieldKey}`}
        >
          {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          <span className="font-medium text-foreground/90">{label}</span>
          {levelBadge}
          {compactSummary}
          {!compactSummary && visible.length > 0 && <span className="text-[10px] text-gray-500">{visible.length} note{visible.length === 1 ? "" : "s"}</span>}
        </button>
        {/* + button to add a new note. Hidden when an edit/add panel is open
            to keep the row tidy. */}
        {open && !adding && !compactSummary && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setAdding(true); }}
            className="ml-2 inline-flex items-center justify-center w-6 h-6 rounded-full text-foreground underline-offset-2 hover:bg-blue-50 hover:text-foreground"
            title="Add a new note"
            data-testid={`lineout-add-button-${fieldKey}`}
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {open && (
        <div className="px-2 pb-2 space-y-2">
          {adding && (
            <div className="rounded border border-blue-200 bg-blue-50/40 p-2">
              {autoFill && visible.length === 0 && (
                <div className="text-[10px] text-foreground underline-offset-2 mb-1 inline-flex items-center gap-1">
                  <span className="font-semibold">Auto-suggested:</span>
                  <span className="text-gray-500">edit before saving if needed</span>
                </div>
              )}
              <Textarea
                autoFocus
                value={draft}
                onChange={(e) => { setDraft(e.target.value); onDraftChange && onDraftChange(fieldKey, e.target.value); }}
                placeholder={autoFill || `New ${label.toLowerCase()} note…`}
                className="text-[12px] min-h-[60px]"
                data-testid={`lineout-new-${fieldKey}`}
              />
              <div className="flex justify-end mt-1.5 gap-1.5">
                <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={() => { setAdding(false); setDraft(""); onDraftChange && onDraftChange(fieldKey, ""); }}>Cancel</Button>
                <Button size="sm" className="h-6 text-[11px] bg-blue-600 hover:bg-blue-700 text-white" disabled={!draft.trim() || saving} onClick={submit} data-testid={`lineout-add-${fieldKey}`}>
                  {saving ? "Adding…" : "Add Note"}
                </Button>
              </div>
            </div>
          )}
          {!adding && visible.length === 0 && (
            <div className="text-[11px] text-gray-400 italic">No notes yet — click + to add one.</div>
          )}
          {visible.map((n, idx) => {
            const isLatest = idx === 0; // newest-on-top
            const editing = editingId === n.id;
            return (
              <div
                key={n.id}
                className={`rounded border px-2 py-1.5 ${isLatest ? "bg-amber-50 border-amber-200" : "bg-gray-50 border-gray-200"}`}
                data-testid={`lineout-entry-${fieldKey}-${idx}`}
              >
                <div className="flex items-center justify-between text-[10px] text-gray-500 mb-0.5">
                  <span>
                    <span className="font-semibold text-foreground/80">{n.author || "—"}</span>
                    <span className="mx-1">·</span>
                    {fmtTimestamp(n.created_at)}
                    {n.edited_at && <span className="ml-1 italic">(edited {fmtTimestamp(n.edited_at)})</span>}
                    {isLatest && <span className="ml-2 inline-flex items-center text-[9px] uppercase font-semibold text-amber-700 bg-amber-100 border border-amber-200 rounded px-1">Latest</span>}
                  </span>
                  {!editing && (
                    <button
                      type="button"
                      onClick={() => { setEditingId(n.id); setEditDraft(n.text); }}
                      className="text-gray-400 hover:text-foreground/80 inline-flex items-center gap-1"
                      data-testid={`lineout-edit-${fieldKey}-${idx}`}
                      title="Edit"
                    >
                      <Pencil className="w-2.5 h-2.5" /> edit
                    </button>
                  )}
                </div>
                {!editing ? (
                  <div className="text-[12px] text-foreground/90 whitespace-pre-wrap">{n.text}</div>
                ) : (
                  <div className="space-y-1.5">
                    <Textarea
                      value={editDraft}
                      onChange={(e) => setEditDraft(e.target.value)}
                      className="text-[12px] min-h-[60px]"
                      data-testid={`lineout-edit-textarea-${fieldKey}-${idx}`}
                    />
                    <div className="flex justify-end gap-1.5">
                      <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={() => { setEditingId(null); setEditDraft(""); }}>Cancel</Button>
                      <Button size="sm" className="h-6 text-[11px] bg-blue-600 hover:bg-blue-700 text-white" onClick={() => saveEdit(n.id)} data-testid={`lineout-edit-save-${fieldKey}-${idx}`}><Save className="w-2.5 h-2.5 mr-1" />Save</Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function MobPopover({ open, onOpenChange, mob, project, scopeCode, onChanged, onUnschedule, onDispatch, onDelete }) {
  const { user: authUser } = useAuth();
  const navigate = useNavigate();
  const [installers, setInstallers] = useState([]);
  const [pms, setPms] = useState([]);
  const [clients, setClients] = useState([]);
  const [frsItems, setFrsItems] = useState([]);
  const [notes, setNotes] = useState([]);
  const [isPunchlist, setIsPunchlist] = useState(false);
  const [specialNotes, setSpecialNotes] = useState("");
  const [mobIssues, setMobIssues] = useState([]);
  const loadMobIssues = async () => {
    if (!mob?.id) return;
    try {
      const { data } = await api.get("/issues", { params: { mobilization_id: mob.id } });
      setMobIssues(data || []);
    } catch { setMobIssues([]); }
  };
  // Auto-fill state for "Last Area Worked In" — pulled from the most recent
  // dispatched assignment + its mob's FRS location.
  const [lastDispatched, setLastDispatched] = useState(null);
  // Master Expand/Collapse-All control for everything inside the Lineout
  // section. `undefined` = each child uses its own default-open state;
  // `true` = force every child open; `false` = force every child closed.
  const [lineoutAllOpen, setLineoutAllOpen] = useState(undefined);
  // Map of full scope name → short code (e.g. "Toilet Room Accessories" → "TRA")
  // used to render the short pill in the popover header.
  const [scopeMap, setScopeMap] = useState({});

  // Resolve mob's primary scope from scope_type (first comma-separated value)
  const mobScope = (mob?.scope_type || "").split(",")[0].trim();
  // Short code for the header pill — falls back to first 4 letters when not in settings.
  const mobScopeShort = scopeMap[mobScope.toLowerCase()] || mobScope.split(" ").map((w) => w[0]).join("").slice(0, 4).toUpperCase();

  useEffect(() => {
    if (!open || !mob?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const [insts, pmsData, clientsData, notesData, lastDisp, scopeRes] = await Promise.all([
          api.get("/installers"),
          api.get("/pms"),
          api.get("/clients"),
          api.get("/notes", { params: { project_id: mob.project_id } }),
          api.get(`/projects/${mob.project_id}/last-dispatched`).catch(() => ({ data: {} })),
          api.get("/scope-codes").catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setInstallers(insts.data || []);
        setPms(pmsData.data || []);
        setClients(clientsData.data || []);
        setNotes(notesData.data || []);
        setLastDispatched(lastDisp.data || null);
        const m = {};
        for (const s of (scopeRes.data || [])) {
          if (s.code && s.trade) m[s.trade.toLowerCase()] = s.code;
          if (s.code) m[s.code.toLowerCase()] = s.code;
        }
        setScopeMap(m);
        if ((mob.frs_item_ids || []).length > 0) {
          const r = await api.get(`/frs-items`, { params: { project_id: mob.project_id } });
          if (cancelled) return;
          setFrsItems((r.data || []).filter((i) => mob.frs_item_ids.includes(i.id)));
        } else {
          setFrsItems([]);
        }
      } catch { /* ignore */ }
      setIsPunchlist(!!mob.is_punchlist);
      setSpecialNotes(mob.special_notes || "");
      loadMobIssues();
    })();
    return () => { cancelled = true; };
  }, [open, mob?.id]); // eslint-disable-line

  if (!mob) return null;
  const foreman = installers.find((i) => i.id === project?.foreman_id);
  const capableInstallers = (mob.capable_installer_ids || [])
    .map((id) => installers.find((i) => i.id === id))
    .filter(Boolean);
  const pm = pms.find((p) => p.id === project?.pm_id);
  const client = clients.find((c) => c.id === project?.client_id);
  // Notes are signed by the person, not their role.
  const currentUser = authUser?.email || authUser?.name || "";

  const fullAddress = [project?.address, project?.city, project?.state, project?.zip_code].filter(Boolean).join(", ");
  const mapsLink = fullAddress
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`
    : null;

  // Compact label for the Materials accordion header — shown next to the
  // collapse caret so the user sees totals without expanding.
  const materialsSummary = (() => {
    const code = scopeCode || mobScope.slice(0, 3).toUpperCase();
    const total = mob.total_quantity || frsItems.reduce((s, i) => s + Number(i.quantity || 0), 0);
    return `${code || "—"} ${total}u`;
  })();

  const togglePunchlist = async () => {
    const next = !isPunchlist;
    setIsPunchlist(next);
    try {
      await api.patch(`/mobilizations/${mob.id}`, { is_punchlist: next });
      onChanged && onChanged({ is_punchlist: next });
      toast.success(next ? "Marked as punchlist" : "Removed from punchlist");
    } catch {
      setIsPunchlist(!next);
      toast.error("Could not update");
    }
  };

  const onNoteAdded = (n) => setNotes((cur) => [...cur, n]);
  const onNoteEdited = (n) => setNotes((cur) => cur.map((x) => x.id === n.id ? n : x));

  const saveSpecialNotes = async () => {
    const text = specialNotes.trim();
    if (text === (mob.special_notes || "").trim()) return;
    try {
      await api.patch(`/mobilizations/${mob.id}`, { special_notes: text });
      onChanged && onChanged({ special_notes: text });
      toast.success("Special notes saved");
    } catch {
      toast.error("Could not save special notes");
    }
  };

  // Compute auto-fill values for Daily Target and Last Area Worked In.
  // Daily Target = ceil( total_units / scheduled workdays ); falls back to
  // round( total_units / total_hours * 10 ) when no dates are set yet.
  const totalUnits = mob.total_quantity || frsItems.reduce((s, i) => s + Number(i.quantity || 0), 0);
  const totalHours = Number(mob.estimated_duration_hours) || 0;
  const schedDays = workdaysBetween(
    mob.scheduled_start_date || mob.requested_start_date,
    mob.scheduled_end_date || mob.requested_end_date
  );
  const dailyTargetCalc = totalUnits > 0
    ? (schedDays > 0
        ? Math.max(1, Math.ceil(totalUnits / schedDays))
        : (totalHours > 0 ? Math.max(1, Math.round((totalUnits / totalHours) * 10)) : null))
    : null;
  const dailyTargetTip = schedDays > 0
    ? `Auto: ${totalUnits} units ÷ ${schedDays} workday${schedDays === 1 ? "" : "s"} = ${dailyTargetCalc} units / day`
    : `Auto: ${totalUnits} units ÷ ${totalHours}h × 10 = ${dailyTargetCalc} units / day (set dates to use units ÷ days)`;
  const autoFillFor = (key) => {
    if (key === "daily_target") {
      return dailyTargetCalc != null ? `${dailyTargetCalc} units / day (${dailyTargetTip.toLowerCase()})` : "";
    }
    if (key === "last_area") {
      return lastDispatched?.last_area || "";
    }
    return "";
  };

  // Daily Target sits inline (its own DailyTargetField). All other note
  // fields render in the bottom block of the Lineout section.
  const fieldsBelow = LINEOUT_FIELDS.filter((f) => f.placement !== "after_materials");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl p-0 overflow-hidden max-h-[92vh] flex flex-col"
        data-testid="mob-popover"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader className={`px-4 py-3 border-b border-gray-200 ${isPunchlist ? "bg-orange-100 dark:bg-orange-950/40" : "bg-muted/60"}`}>
          <div className="flex items-start gap-2">
            <span className={`mt-1 w-2 h-2 rounded-full ${isPunchlist ? "bg-orange-500" : mob.status === "ready_to_dispatch" ? "bg-emerald-500" : "bg-foreground"}`} />
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-base font-bold text-foreground truncate">
                {project?.project_name || mob.title}
                {isPunchlist && (
                  <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase font-semibold text-orange-700 bg-orange-200 border border-orange-300 rounded-full px-2 py-0.5 align-middle">
                    <Wrench className="w-3 h-3" /> Punchlist
                  </span>
                )}
                {mob.is_auto_request && (
                  <span
                    className="ml-2 inline-flex items-center text-[10px] uppercase font-semibold text-violet-700 bg-violet-100 border border-violet-300 rounded-full px-2 py-0.5 align-middle"
                    title="Auto-requested by the readiness window rule"
                    data-testid="mob-popover-auto-badge"
                  >
                    Auto
                  </span>
                )}
                {mob.lineout_ready === false && (
                  <span
                    className="ml-2 inline-flex items-center text-[10px] uppercase font-semibold text-red-700 bg-red-100 border border-red-300 rounded-full px-2 py-0.5 align-middle"
                    title='Dispatch is blocked until the "Parking and Site Logistics" lineout note has content'
                    data-testid="mob-popover-lineout-required"
                  >
                    Lineout required
                  </span>
                )}
              </DialogTitle>
              <div className="flex items-center gap-3 text-[11px] text-foreground/80 mt-0.5 flex-wrap">
                {project?.job_number && <span className="font-mono font-semibold text-foreground">{project.job_number}</span>}
                {/* Inline date editors. Saving either field PATCHes the mob;
                    when end < start we coerce end = start. The Schedule
                    board's parent re-fetches via onChanged. */}
                <ScheduleDateEditor
                  mob={mob}
                  onChanged={onChanged}
                />
                {mob.estimated_duration_hours ? <span>{Math.round(mob.estimated_duration_hours)}h</span> : null}
                <span className="capitalize">{(mob.status || "").replace(/_/g, " ")}</span>
                {mobScope && (
                  <span className="text-purple-700 font-mono font-semibold uppercase tracking-wide" data-testid="mob-popover-scope-short">
                    {mobScopeShort}
                  </span>
                )}
              </div>
            </div>
          </div>
        </DialogHeader>
        <div className="px-4 pt-3" data-testid="mob-popover-readiness">
          <CardReadiness mobId={mob.id} projectId={mob.project_id} onChanged={() => onChanged?.({})} />
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto">
          {/* Identity block */}
          <div className="px-4 py-3 space-y-1.5 text-[12px]">
            {/* Scope — long-form version of the short code shown in the header. */}
            {mobScope && (
              <div className="flex items-center gap-2">
                <span className="text-gray-500 w-20 shrink-0">Scope:</span>
                <span className="font-medium text-foreground" data-testid="mob-popover-scope-long">{mobScope}</span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <span className="text-gray-500 w-20 shrink-0">Client:</span>
              <span className="font-medium text-foreground">{client?.name || project?.project_name || "—"}</span>
              {client?.rating && <span className="text-[10px] uppercase font-semibold text-gray-500 bg-gray-100 border border-gray-200 rounded px-1.5">{client.rating}</span>}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-500 w-20 shrink-0">GC Contact:</span>
              <span className="font-medium text-foreground/90">{project?.gc_contact_name || "—"}</span>
              {project?.gc_contact_phone && <a href={`tel:${project.gc_contact_phone}`} className="inline-flex items-center gap-1 text-foreground underline-offset-2 hover:underline" data-testid="mob-gc-phone"><Phone className="w-3 h-3" />{project.gc_contact_phone}</a>}
              {project?.gc_contact_email && <a href={`mailto:${project.gc_contact_email}`} className="inline-flex items-center gap-1 text-foreground underline-offset-2 hover:underline" data-testid="mob-gc-email"><Mail className="w-3 h-3" />{project.gc_contact_email}</a>}
            </div>
            <div className="flex items-start gap-2">
              <span className="text-gray-500 w-20 shrink-0 mt-0.5">Address:</span>
              {mapsLink ? (
                <a href={mapsLink} target="_blank" rel="noopener noreferrer" className="text-foreground underline-offset-2 hover:underline inline-flex items-center gap-1" data-testid="mob-address">
                  <MapPin className="w-3 h-3" />{fullAddress}
                </a>
              ) : (
                <span className="text-gray-400">— (set in Job)</span>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-gray-500 w-20 shrink-0">Foreman:</span>
              {foreman ? (
                <>
                  <span className="font-medium text-foreground inline-flex items-center gap-1"><User className="w-3 h-3" />{foreman.name}</span>
                  {foreman.phone && <a href={`tel:${foreman.phone}`} className="text-foreground underline-offset-2 hover:underline inline-flex items-center gap-1" data-testid="mob-foreman-phone"><Phone className="w-3 h-3" />{foreman.phone}</a>}
                  {foreman.email && <a href={`mailto:${foreman.email}`} className="text-foreground underline-offset-2 hover:underline inline-flex items-center gap-1" data-testid="mob-foreman-email"><Mail className="w-3 h-3" />{foreman.email}</a>}
                </>
              ) : <span className="text-gray-400">— (assign in Job)</span>}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-gray-500 w-20 shrink-0">PM:</span>
              {pm ? (
                <>
                  <span className="font-medium text-foreground inline-flex items-center gap-1"><User className="w-3 h-3" />{pm.name}</span>
                  {pm.phone && <a href={`tel:${pm.phone}`} className="text-foreground underline-offset-2 hover:underline inline-flex items-center gap-1" data-testid="mob-pm-phone"><Phone className="w-3 h-3" />{pm.phone}</a>}
                  {pm.email && <a href={`mailto:${pm.email}`} className="text-foreground underline-offset-2 hover:underline inline-flex items-center gap-1" data-testid="mob-pm-email"><Mail className="w-3 h-3" />{pm.email}</a>}
                </>
              ) : project?.pm_name ? (
                <span className="text-foreground/80">{project.pm_name}</span>
              ) : (
                <span className="text-gray-400">— (assign in Job)</span>
              )}
            </div>
            <div className="flex items-start gap-2 pt-1" data-testid="mob-crew-row">
              <span className="text-gray-500 w-20 shrink-0 mt-0.5">Crew:</span>
              <div className="min-w-0">
                <span className="font-medium text-foreground">
                  {mob.num_installers || 2} installer{(mob.num_installers || 2) === 1 ? "" : "s"} required
                </span>
                {mob.crew_sizing_note && (
                  <span className="ml-2 text-[11px] text-gray-500" data-testid="mob-crew-sizing-note">
                    {mob.crew_sizing_note}
                  </span>
                )}
                {mob.gc_date_conflict && (
                  <span
                    className="ml-2 text-[10px] uppercase font-semibold text-red-700 bg-red-50 border border-red-200 rounded px-1.5 py-0.5"
                    title="Even at the crew cap this scope finishes after the GC completion date"
                    data-testid="mob-gc-conflict-badge"
                  >
                    misses GC
                  </span>
                )}
                <div className="text-[11px] text-gray-600 mt-0.5" data-testid="mob-capable-installers">
                  {capableInstallers.length > 0 ? (
                    <>
                      <span className="text-gray-500">Capable ({capableInstallers.length}): </span>
                      {capableInstallers.slice(0, 6).map((i) => i.name).join(", ")}
                      {capableInstallers.length > 6 ? ` +${capableInstallers.length - 6} more` : ""}
                    </>
                  ) : (
                    <span className="text-gray-400">No installers flagged capable of this scope in Settings</span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 pt-1.5">
              <label className="flex items-center gap-2 cursor-pointer" data-testid="mob-punchlist-label">
                <input
                  type="checkbox"
                  checked={isPunchlist}
                  onChange={togglePunchlist}
                  className="w-4 h-4"
                  data-testid="mob-punchlist-toggle"
                />
                <span className="text-[12px] font-medium text-foreground/90 inline-flex items-center gap-1">
                  <Wrench className="w-3 h-3" /> Punchlist
                </span>
              </label>
            </div>
            {(isPunchlist || mob.is_punchlist) && (
              <div className="pt-1.5">
                <div className="text-gray-500 text-[11px] mb-1">Material needed (punch)</div>
                <input
                  defaultValue={mob.punch_material || ""}
                  onBlur={async (e) => {
                    const v = e.target.value;
                    if (v === (mob.punch_material || "")) return;
                    try {
                      await api.patch(`/mobilizations/${mob.id}`, { punch_material: v });
                      onChanged && onChanged({ punch_material: v });
                      toast.success("Material noted");
                    } catch { toast.error("Could not save the material note"); }
                  }}
                  placeholder="e.g. 2 boxes of anchors, 1 mirror"
                  className="w-full text-[12px] border border-gray-300 rounded-md px-2 py-1.5 bg-white"
                  data-testid="mob-punch-material-input"
                />
              </div>
            )}
            <div className="pt-1.5">
              <div className="text-gray-500 text-[11px] mb-1">Special notes</div>
              <textarea
                value={specialNotes}
                onChange={(e) => setSpecialNotes(e.target.value)}
                onBlur={saveSpecialNotes}
                rows={2}
                placeholder="Anything the crew must know about this card…"
                className="w-full text-[12px] border border-gray-300 rounded-md px-2 py-1.5 bg-white"
                data-testid="mob-special-notes-input"
              />
            </div>
          </div>

          {/* Lineout — Daily Target + Materials + note fields. Default
              EXPANDED so users see the section headers immediately;
              individual sub-sections stay collapsed (click to reveal notes). */}
          <Section
            label="Lineout"
            defaultOpen={true}
            testid="mob-lineout-section"
            headerExtra={
              <button
                type="button"
                onClick={() => setLineoutAllOpen((v) => (v === true ? false : true))}
                className="text-[10px] text-gray-500 hover:text-foreground/90 underline"
                data-testid="mob-lineout-toggle-all"
              >
                {lineoutAllOpen === true ? "Collapse all" : "Expand all"}
              </button>
            }
          >
            <div className="space-y-2">
              {/* Daily Target — sits at the top of the Lineout block. Inline
                  click-to-edit value with hover tooltip showing the math. */}
              {dailyTargetCalc != null && (
                <DailyTargetField
                  projectId={mob.project_id}
                  mobScopeType={mobScope}
                  notes={notes}
                  currentUser={currentUser}
                  computedValue={dailyTargetCalc}
                  totalUnits={totalUnits}
                  totalHours={totalHours}
                  mathTip={dailyTargetTip}
                  onAdded={onNoteAdded}
                  onEdited={onNoteEdited}
                />
              )}

              {/* Materials accordion — Floor → Room → Parts/Qty drill-down */}
              <div className="border border-gray-100 rounded">
                <MaterialsSubSection
                  materialsSummary={materialsSummary}
                  totalRooms={mob.total_rooms || 0}
                  frsItems={frsItems}
                  openOverride={lineoutAllOpen}
                />
              </div>

              {/* Other note fields — equal vertical spacing via space-y-2 */}
              {fieldsBelow.map((f) => (
                <NoteHistoryField
                  key={f.key}
                  projectId={mob.project_id}
                  mobScopeType={mobScope}
                  fieldKey={f.key}
                  label={f.label}
                  level={f.level}
                  autoFill={f.auto ? autoFillFor(f.auto) : ""}
                  notes={notes}
                  currentUser={currentUser}
                  onAdded={onNoteAdded}
                  onEdited={onNoteEdited}
                  openOverride={lineoutAllOpen}
                />
              ))}
            </div>
          </Section>

          {/* Compliance / requirements */}
          {(mob.badged_required || mob.drug_test_required || mob.site_familiarity_required || mob.foreman_notes) && (
            <Section label="Requirements & Notes" defaultOpen={false} testid="mob-requirements-section">
              <div className="text-[11px] space-y-1">
                {mob.badged_required && <div className="inline-flex items-center gap-1 text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5 mr-1"><AlertTriangle className="w-3 h-3" /> Badged required</div>}
                {mob.drug_test_required && <div className="inline-flex items-center gap-1 text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5 mr-1"><AlertTriangle className="w-3 h-3" /> Drug-test required</div>}
                {mob.site_familiarity_required && <div className="inline-flex items-center gap-1 text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5 mr-1"><AlertTriangle className="w-3 h-3" /> Site-familiar / lineouts required</div>}
                {mob.foreman_notes && <div className="mt-2 text-foreground/80 whitespace-pre-wrap">{mob.foreman_notes}</div>}
              </div>
            </Section>
          )}
          <Section
            label="Issues"
            count={mobIssues.filter((i) => i.status !== "resolved").length}
            defaultOpen={mobIssues.some((i) => i.status !== "resolved")}
            testid="mob-issues-section"
          >
            {mobIssues.length === 0 ? (
              <div className="text-[11px] text-gray-400" data-testid="mob-issues-empty">
                No issues raised on these rooms.
              </div>
            ) : mobIssues.map((i) => (
              <IssueRow key={i.id} issue={i} compact onChanged={loadMobIssues} />
            ))}
          </Section>
        </div>

        <div className="border-t border-gray-200 px-3 py-2 bg-gray-50 flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-[11px] border-orange-300 text-orange-800 hover:bg-orange-50"
            onClick={() => { onOpenChange(false); navigate(`/install?project=${mob.project_id}&mob=${mob.id}`); }}
            data-testid="mob-popover-open-frs"
            title="Open the Install FRS filtered to this card's scope and rooms"
          >
            <HardHat className="w-3 h-3 mr-1" /> Open FRS
          </Button>
          {onDispatch && (
            <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => { onDispatch(); onOpenChange(false); }} data-testid="mob-popover-dispatch">
              <Send className="w-3 h-3 mr-1" /> Send to Dispatch
            </Button>
          )}
          {onDelete && (
            <Button size="sm" variant="outline" className="h-7 text-[11px] text-red-600 border-red-200 hover:bg-red-50 ml-auto" onClick={() => { onDelete(); onOpenChange(false); }} data-testid="mob-popover-delete">
              <Trash2 className="w-3 h-3 mr-1" /> Delete Mob
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Inline date-range editor in the popover header. Saves on blur; when end <
// start we coerce end = start. Recomputes total_hours = days × num_installers
// × 10 server-side via the existing PATCH route's day-math is on the client,
// so we just send the new dates.
function ScheduleDateEditor({ mob, onChanged }) {
  const [start, setStart] = useState(mob.scheduled_start_date || mob.requested_start_date || "");
  const [end, setEnd] = useState(mob.scheduled_end_date || mob.requested_end_date || mob.scheduled_start_date || "");
  useEffect(() => {
    setStart(mob.scheduled_start_date || mob.requested_start_date || "");
    setEnd(mob.scheduled_end_date || mob.requested_end_date || mob.scheduled_start_date || "");
  }, [mob.id, mob.scheduled_start_date, mob.scheduled_end_date]);
  const save = async (nextStart, nextEnd) => {
    if (!nextStart) return;
    let s = nextStart;
    let e = nextEnd && nextEnd >= s ? nextEnd : s;
    if (s === (mob.scheduled_start_date || "") && e === (mob.scheduled_end_date || "")) return;
    try {
      // Re-compute total hours from days × num_installers × 10
      const days = Math.max(1, Math.round((new Date(e) - new Date(s)) / 86400000) + 1);
      const crew = Math.max(1, Number(mob.num_installers) || 1);
      const hours = days * crew * 10;
      await api.patch(`/mobilizations/${mob.id}`, {
        scheduled_start_date: s,
        scheduled_end_date: e,
        estimated_duration_hours: hours,
        // If the mob was sitting in the pool (status=requested), promote it
        status: mob.status === "requested" ? "scheduled" : mob.status,
      });
      onChanged && onChanged({
        scheduled_start_date: s,
        scheduled_end_date: e,
        estimated_duration_hours: hours,
        status: mob.status === "requested" ? "scheduled" : mob.status,
      });
      toast.success("Schedule updated");
    } catch { toast.error("Could not update schedule"); }
  };
  return (
    <span className="inline-flex items-center gap-1">
      <input
        type="date"
        value={start}
        onChange={(e) => setStart(e.target.value)}
        onBlur={() => save(start, end)}
        className="text-[11px] border border-gray-200 rounded px-1 py-0.5 bg-white hover:border-blue-400 focus:outline-none focus:border-blue-500"
        data-testid="mob-popover-start-date"
        title="Scheduled start date"
      />
      <span className="text-gray-400">→</span>
      <input
        type="date"
        value={end || start}
        min={start}
        onChange={(e) => setEnd(e.target.value)}
        onBlur={() => save(start, end || start)}
        className="text-[11px] border border-gray-200 rounded px-1 py-0.5 bg-white hover:border-blue-400 focus:outline-none focus:border-blue-500"
        data-testid="mob-popover-end-date"
        title="Scheduled end date"
      />
    </span>
  );
}

export function MaterialsSubSection({ materialsSummary, totalRooms, frsItems, openOverride }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (openOverride !== undefined) setOpen(!!openOverride);
  }, [openOverride]);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-2 py-1.5 text-[12px] hover:bg-gray-50"
        data-testid="mob-materials-section"
      >
        <span className="flex items-center gap-1.5">
          {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          <span className="font-medium text-foreground/90">Materials</span>
          <span className="text-[11px] text-gray-500">{materialsSummary} · {totalRooms} room{totalRooms === 1 ? "" : "s"}</span>
        </span>
      </button>
      {open && (
        <div className="px-2 pb-2">
          <MaterialsBreakdown frsItems={frsItems} />
        </div>
      )}
    </>
  );
}

// Per-section lineout breakdown rendered at the END of the popover. For each
// FRS room (Building · Floor · Room) shows the parts table; below all rooms
// shows the dated note feed (newest first) for every lineout key + project-
// level entries, mirroring exactly what the daily lineout email delivers.
const LINEOUT_LABEL_MAP = {
  parking_logistics: "Parking and Site Logistics",
  current_install_plan: "Special Instructions",
  daily_target: "Daily Target",
  installer_notes: "Important Installer Notes",
  last_area_worked_in: "Last Area Worked In",
};

export function LineoutBreakdown({ frsItems, notes, mobScopeType }) {
  const groups = useMemo(() => {
    const m = new Map();
    for (const it of frsItems || []) {
      const key = `${it.building || ""}||F${it.floor || ""}||${it.room_number || ""}`;
      if (!m.has(key)) {
        m.set(key, {
          key, building: it.building || "", floor: it.floor || "",
          room: it.room_number || "", items: [], totalQty: 0,
        });
      }
      const g = m.get(key);
      g.items.push(it);
      g.totalQty += Number(it.quantity || 0);
    }
    return [...m.values()].sort((a, b) =>
      (a.building || "").localeCompare(b.building || "") ||
      String(a.floor).localeCompare(String(b.floor)) ||
      String(a.room).localeCompare(String(b.room)),
    );
  }, [frsItems]);

  // Filter notes for this mob: project-level (scope_type === "") OR scope-level
  // matching this mob's scope. Group by `key` (parking, current_install_plan,
  // installer_notes, …); within each group, sort newest-first.
  const noteSections = useMemo(() => {
    const out = {};
    for (const n of (notes || [])) {
      const matches = (n.scope_type === "") || (n.scope_type === (mobScopeType || ""));
      if (!matches) continue;
      const k = n.key || "other";
      if (!out[k]) out[k] = [];
      out[k].push(n);
    }
    for (const k of Object.keys(out)) {
      out[k].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    }
    return out;
  }, [notes, mobScopeType]);

  // Strip "Building "/"Bldg " noise so the popover header reads "BB · F1 · Rm 104"
  // — matching the smart-title format used in the email + dispatch pool.
  const _normBldg = (v) => {
    if (v === null || v === undefined) return "";
    let s = String(v).trim();
    s = s.replace(/^Building\s+/i, "").replace(/^Bldg\s+/i, "");
    return s;
  };
  const orderedKeys = Object.keys(LINEOUT_LABEL_MAP).filter((k) => noteSections[k]);
  if (groups.length === 0 && orderedKeys.length === 0) {
    return <div className="text-xs text-gray-400 italic">No FRS items linked yet · no notes recorded.</div>;
  }
  return (
    <div className="space-y-3">
      {groups.length > 0 && (
        <div className="space-y-1.5">
          {groups.map((g) => {
            const bldg = _normBldg(g.building);
            const labelBits = [];
            if (bldg) labelBits.push(`B${bldg}`);
            if (g.floor !== "" && g.floor !== null && g.floor !== undefined) labelBits.push(`F${g.floor}`);
            if (g.room !== "" && g.room !== null && g.room !== undefined) labelBits.push(`Rm ${g.room}`);
            const label = labelBits.join(" · ") || "(no location)";
            return (
            <div key={g.key} className="border border-gray-100 rounded overflow-hidden" data-testid={`mob-lineout-section-${g.key}`}>
              <div className="px-2 py-1 bg-gray-50 text-[11px] font-semibold text-foreground/90 flex items-center justify-between">
                <span className="flex items-center gap-1.5">
                  <Building2 className="w-3 h-3 text-gray-500" />
                  {label}
                </span>
                <span className="text-[10px] text-gray-500 font-normal">{g.items.length} line{g.items.length === 1 ? "" : "s"} · {g.totalQty}u</span>
              </div>
              <table className="w-full text-[11px]">
                <thead className="bg-white text-gray-500">
                  <tr>
                    <th className="text-left px-2 py-0.5 font-medium">Description</th>
                    <th className="text-left px-2 py-0.5 font-medium w-20">Part</th>
                    <th className="text-right px-2 py-0.5 font-medium w-12">Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {g.items.map((i) => (
                    <tr key={i.id} className="border-t border-gray-100">
                      <td className="px-2 py-0.5 text-foreground/80">{i.product_description}</td>
                      <td className="px-2 py-0.5 text-gray-500 font-mono">{i.product_code || "—"}</td>
                      <td className="px-2 py-0.5 text-right font-medium">{i.quantity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            );
          })}
        </div>
      )}
      {orderedKeys.length > 0 && (
        <div className="space-y-2 pt-2 border-t border-gray-100">
          {orderedKeys.map((k) => (
            <div key={k} data-testid={`mob-lineout-notes-${k}`}>
              <div className="text-[11px] font-semibold text-foreground/80 mb-1 flex items-center gap-1">
                <Pencil className="w-3 h-3 text-gray-400" /> {LINEOUT_LABEL_MAP[k]}
              </div>
              <div className="space-y-1">
                {noteSections[k].map((n) => (
                  <div key={n.id} className="text-[11px] border-l-2 border-blue-300 bg-slate-50 px-2 py-1 rounded-r">
                    <div className="text-[10px] text-gray-500">
                      {fmtTimestamp(n.created_at)}
                      {n.author ? ` · ${n.author}` : ""}
                      {n.scope_type ? " · scope" : " · job"}
                    </div>
                    <div className="text-foreground/90 whitespace-pre-wrap">{n.text}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Daily Target row — inline value next to the label with a hover tooltip
// showing the math (units ÷ hours × 10). The current value defaults to the
// auto-calculated number; clicking it reveals an inline input that, when
// saved, appends a new "daily_target" note for this scope.
export function DailyTargetField({
  projectId, mobScopeType, notes, currentUser,
  computedValue, totalUnits, totalHours, mathTip,
  onAdded,
}) {
  const visible = useMemo(() => {
    return (notes || [])
      .filter((n) => n.key === "daily_target" && n.scope_type === (mobScopeType || ""))
      .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  }, [notes, mobScopeType]);
  const latest = visible[0];
  const displayValue = latest ? latest.text : `${computedValue} units / day`;
  const tip = mathTip || `Auto: ${totalUnits} units ÷ ${totalHours}h × 10 = ${computedValue} units / day`;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const startEdit = () => {
    setDraft(latest ? latest.text : `${computedValue} units / day`);
    setEditing(true);
  };
  const submit = async () => {
    const next = draft.trim();
    if (!next) { setEditing(false); return; }
    setSaving(true);
    try {
      const r = await api.post("/notes", {
        project_id: projectId,
        scope_type: mobScopeType || "",
        key: "daily_target",
        text: next,
        author: currentUser || "",
      });
      onAdded && onAdded(r.data);
      setEditing(false);
      setDraft("");
      toast.success("Daily target updated");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="border border-gray-100 rounded">
      <div className="flex items-center justify-between gap-2 px-2 py-1.5 hover:bg-gray-50">
        <span className="flex items-center gap-1.5 text-[12px]">
          <span className="font-medium text-foreground/90">Daily Target</span>
          {!editing ? (
            <button
              type="button"
              onClick={startEdit}
              title={tip}
              data-testid="lineout-daily-target-value"
              className="text-[12px] font-semibold text-foreground hover:text-foreground hover:underline px-1.5 py-0.5 rounded"
            >
              {displayValue}
            </button>
          ) : (
            <span className="inline-flex items-center gap-1">
              <input
                autoFocus
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") submit();
                  else if (e.key === "Escape") { setEditing(false); setDraft(""); }
                }}
                placeholder={`${computedValue} units / day`}
                className="text-[12px] border border-blue-300 rounded px-1.5 py-0.5 focus:outline-none focus:border-blue-500 w-44"
                data-testid="lineout-daily-target-input"
              />
              <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={() => { setEditing(false); setDraft(""); }}>Cancel</Button>
              <Button size="sm" className="h-6 text-[11px] bg-blue-600 hover:bg-blue-700 text-white" disabled={!draft.trim() || saving} onClick={submit} data-testid="lineout-daily-target-save">
                {saving ? "…" : "Save"}
              </Button>
            </span>
          )}
          {!editing && !latest && (
            <span className="text-[10px] text-gray-400" data-testid="lineout-daily-target-math">{tip}</span>
          )}
        </span>
        {!editing && latest && (
          <span className="text-[10px] text-gray-500" title={`${latest.author || "—"} · ${fmtTimestamp(latest.created_at)}`}>
            edited {fmtTimestamp(latest.created_at)}
          </span>
        )}
      </div>
    </div>
  );
}
